import type { BaseTranslation } from '../../i18n-types';

const learn: BaseTranslation = {
  agreementPopup: {
    agreementConfirmation: 'Please be aware that by clicking on <>"I agree"<>,',
    button: {
      agree: "I agree, let's continue",
      disagree: 'No thanks',
    },
    joiningNetwork:
      'You are about to join our network of dermo-professionals, library of cases and much more.',
    readPrivacyAndTerms: {
      and: 'and',
      privacyPolicy: 'Privacy policy',
      readAndUnderstood:
        "you confirm that you have read and understood Dermloop's",
      termsOfUse: 'Terms of use',
    },
    title: 'Terms of Use & Privacy Policy',
  },
  casesScreen: {
    correctnessTag: '{correct:boolean|{true: Correct, *: Incorrect}}',
    difficulty: {
      easy: 'Easy',
      hard: 'Hard',
      medium: 'Medium',
      noDifficulty: 'None',
      veryHard: 'Very hard',
    },
    list: {
      trainingPlaceholder: 'Completed cases will be listed here',
    },
    points: 'Point{{count:s}}',
    title: {
      anamnesis:
        '{gender|{Male: Male, Female: Female, *: Person}} in {gender|{Male: his, Female: her, *: their}} {age:number}s',
    },
    trainingCase: {
      feedbackHeader: {
        difficulty: 'Difficulty',
        partiallyCorrectInfo: {
          title: 'Not correct',
          messageLine1:
            'The answer is <>not correct<>, but belongs to the correct category.',
          messageLine2: '<>0<> points are awarded.',
          dismiss: 'Got it',
        },
        seeExplanation: 'See explanation & tips',
      },
    },
  },
  courseScreen: {
    lesson: {
      type: '{lessonType|{Quiz: Quiz, Reading: Reading}}',
    },
    level: {
      levelNumber: 'Level {level:number}',
    },
  },
  diagnosisStatisticDetailScreen: {
    analyticSection: {
      content: {
        completedCases: 'Completed cases',
        overallAccuracy: 'Accuracy',
      },
      title: 'Statistics for diagnosis group',
    },
    commonMistakeSection: {
      title: 'Your most common mistakes for {diagnosisName:string|lowercase}',
    },
    learnMoreSection: {
      content: {
        readMore: 'Read more about {diagnosisName:string|lowercase}',
      },
      title: 'Learn more',
    },
    subDiagnosisSection: {
      title: '{diagnosisName:string} subdiagnoses',
    },
  },
  general: {
    backText: 'Back',
    homeText: 'Home',
    button: {
      restartApp: 'Restart the app',
    },
    diagnosisItem: {
      read: 'Read',
      whatToRead: 'What would you like to read?',
    },
    doneReading: 'Done reading',
    markAsRead: 'Mark as read',
  },
  homeScreen: {
    dahtNudgePopup: {
      description:
        'You have now reached your goal of dermoscopy training for the DAHT trial. Please go to daht.dermloop.io in order to assess the DAHT cases and finish the trial.',
      buttonText: 'Go to DAHT',
      dismissText: 'Continue in Learn',
      title: 'DAHT training completed',
    },
    controlGroupLocked: {
      title: 'Learn locked',
      description:
        'You will gain access to this app once you have finished the minimum amount of DAHT cases. Go to daht.dermloop.io',
    },
    trainingCard: {
      actionTitle: '{started:boolean|{true: Resume, false: Start}} quiz',
      notificationText:
        'You will soon get notifications when you receive clinical feedback or someone has reacted to a forum-case that you are following.',
      reminderText:
        '🔔 It will also be possible to opt-in for daily reminders on learning goals. 🤓',
    },
    currentLevelCard: {
      currentLevel: 'Current level',
      accuracy: 'Level accuracy',
    },
    trainingStats: {
      title: 'Training progress',
      last7Days: 'Weekly cases',
      total: 'Total cases',
    },
    dahtProgressCard: {
      title: 'My goals',
      dahtStatusIndicator: {
        onTrack: 'On track',
        atRisk: 'At risk',
        challengeEnded: 'Challenge ended',
        challengeCompleted: 'Challenge completed',
      },
      weeklyCases: 'Weekly cases',
      totalProgress: 'Total progress',
      finalProgress: 'Final progress',
      totalCases: 'Total cases',
      studyChallenge: '28-Day study challenge',
      daysLeft: '{days:number} day{{s}} left',
      challengeCompletedMessage:
        'You did it! Thank you for your contribution. Now it is time to evaluate DAHT cases. Please go to the website: daht.dermloop.io',
      challengeEndedMessage:
        "Unfortunately you didn't complete all cases within the timeframe. Feel free to assess the remaining number of cases if you want to evaluate DAHT cases off protocol.",
    },
    articlesProgressCard: {
      title: 'Reading progress',
      articlesCount: '{done:number}/{total:number} articles',
      ongoingArticlesLabel: 'Pick up where you left off',
      getStartedLabel: 'Get started',
      seeAll: 'See all',
      bottomSheetTitle: 'Ongoing articles',
      itemsCount: '{count:number} items',
    },
  },
  loginScreen: {
    loginText: 'Log in / Get started',
    loginFailedMessage:
      "We couldn't log you in. Please try again, or contact support if this keeps happening.",
  },
  settingsScreen: {
    accountSection: {
      content: {
        changePassword: 'Change password',
        changePasswordAlert: {
          message:
            'An email was sent to <>{email:string}<> with instructions on how to change password.',
          title: 'Password reset link sent!',
        },
        changePasswordErrorAlert: {
          message: 'Something went wrong',
          title: 'Oh no!',
        },
        deleteAccount: 'Delete account',
      },
      title: 'Account',
    },
    supportSection: {
      content: {
        contactUs: 'Contact us',
      },
      title: 'Support',
    },
    legalSection: {
      content: {
        privacyPolicy: 'Privacy policy',
        termsOfService: 'Terms of service',
      },
      title: 'Terms and Policies',
    },
    loggedInAs: 'Logged in as',
    logout: 'Log out',
    title: 'Settings',
  },
  moreScreen: {
    title: 'More',
    getHelpSection: {
      title: 'Get help',
      content: {
        reportProblem: 'Report a problem',
        giveFeedback: 'Give us feedback',
      },
    },
    linksSection: {
      title: 'Links and info',
      content: {
        shareLearn: 'Share Learn',
        aboutMelatech: 'About Melatech',
      },
    },
    productsSection: {
      title: 'Explore additional products',
      content: {
        exploreDermloop: 'Explore Dermloop',
        exploreSeranote: 'Explore Seranote',
      },
    },
    feedbackForm: {
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'your.email@example.org',
      cancelButtonLabel: 'Cancel',
      isRequiredLabel: '(required)',
      errorTitle: 'Error',
      formError: 'Please fill out all required fields.',
      emailError: 'Please enter a valid email address.',
      genericError: 'Unable to send feedback due to an unexpected error.',
      successMessageText: 'Thank you for your report!',
      addScreenshotButtonLabel: 'Add screenshot',
      removeScreenshotButtonLabel: 'Remove screenshot',
      captureScreenshotError: 'Failed to capture screenshot',
      reportProblem: {
        formTitle: 'Report a problem',
        messageLabel: 'Description',
        messagePlaceholder: 'Briefly describe the problem...',
        submitButtonLabel: 'Send report',
      },
      giveFeedback: {
        formTitle: 'Give us feedback',
        messageLabel: 'Your feedback',
        messagePlaceholder: 'Share your thoughts with us...',
        submitButtonLabel: 'Send feedback',
      },
    },
  },
  statisticsScreen: {
    cardInfoTitle: {
      completedCases: 'Completed cases',
    },
    levelProgress: {
      accuracy: 'accuracy',
      currentLevel: 'Current level',
      noCasesYet: 'No cases yet',
      points: 'point{{count:s}}',
      seeLess: 'See less',
      seeMore: 'See more',
    },
    diagnosisGroupDetail:
      '{count:number} case{{count:s}} • {accuracy:number}% accuracy',
    sectionTitle: {
      diagnosticAccuracy: 'Diagnostic accuracy',
      diagnosticGroups: 'Diagnostic groups',
      levelProgress: 'Level progress',
    },
  },
  tabs: {
    cases: 'Cases',
    home: 'Home',
    statistics: 'Statistics',
    theory: 'Theory',
  },
  theoryScreen: {
    searchPlaceholder: 'Search for article',
    allArticles: 'All articles',
    readButton: 'Read',
    articleStatus: {
      notStarted: 'Not started',
      inProgress: 'In progress',
      done: 'Done',
    },
  },
  trainingScreen: {
    diagnosisLibrarySearch: 'Search article',
    practiceList: {
      courseSection: {
        itemDescription: 'Course',
        sectionTitle: 'Courses',
      },
      quizSection: {
        itemTitle: 'Practice diagnostics',
        nrOfCase: '{nrOfCases:number} case{{s}}',
        sectionTitle: 'Quick start',
      },
      sectionType: {
        course: 'Course',
        quiz: 'Quiz',
      },
      startButton: '{started:boolean|{true: Resume, *: Start}}',
      title: 'Overview',
    },
    title: {
      diagnosisLibrary: 'Diagnosis library',
      practice: 'Practice',
    },
  },
  trainingSession: {
    anamnesis:
      '{gender|{Male: Male, Female: Female, *: Person}} in {gender|{Male: his, Female: her, *: their}} {age:number}s',
    annotation: {
      noAnnotationCard: {
        continueButton: 'You have requested an explanation',
        description:
          'We do not have annotated dermoscopic criteria for this training lesion. But you can request an explanation and we will annotate the lesion as quickly as possible.',
        requestButton: 'Request explanation',
        title: 'We are sorry!',
      },
      notDisplayed: 'Not displayed',
      title: 'Explanation',
    },
    assessed: {
      correctAnswer: 'Correct answer',
      yourAnswer: 'Your answer',
    },
    backText: 'Back',
    difficultyLevelChange: {
      failure: {
        continueButton: 'Continue on Level {level:number}',
        description:
          'We suggest that you go back to Level {level:number} and practice on easier cases',
        downgradeButton: 'Go back to Level {level:number}',
        title: 'Woops - your accuracy is below {accuracy:number}%!',
      },
      levelDescription: 'Level {level:number} - {levelName:string}!',
      nextLevel: {
        title: 'You are now on',
      },
      progressionRequirement: {
        accuracyForCases: 'Accuracy ({nrOfCases:number} case{{s}})',
        minAccuracy: 'Min. {accuracy:number}%',
        title: 'Requirements to complete the level:',
      },
      success: {
        continueButton: 'Continue',
        description: "You've completed",
        title: 'Congratulations!',
      },
    },
    footerConfirmButton: 'Check answer',
    footerContinueButton: 'Next',
    goback: 'Go back',
    quizError: 'An error occurred while trying to start the quiz',
    quizNumber: 'Quiz #{number:string}',
    reportProblem: {
      cancel: 'Cancel',
      close: 'Close',
      description: 'Please describe the problem',
      problemTextFieldPlaceholder: 'Briefly describe the problem here...',
      submit: 'Send',
      submitted: {
        error:
          'An error occured submitting your report. Our developers have been informed. Please try again.',
        success: 'Your report has been submitted. Thank you for your feedback.',
      },
      title: 'Report a problem',
    },
    selectDiagnosis: 'Select diagnosis',
    statistic: {
      accuracyHeading: 'Accuracy',
      averageDifficulty: 'Average difficulty',
      cases: 'Cases',
      correctAnswers: 'Correct answers',
      correctAnswersRatio: 'Correct answers • {correct:number}/{total:number}',
      courseAccuracyCard: {
        courseFailureButton: 'Retake lesson',
        courseSuccessButton: 'Next lesson',
      },
      level: 'Session level',
      points: '{count:number} point{{s}}',
      quizAccuracyCard: {
        nextSessionButton: 'Next quiz',
      },
      quizResultHeader: 'Session result',
      secondAbbreviated: 'sec.',
      seeMore: 'See more',
      time: 'Time per case',
      timePerCase: 'Time per case (sec.)',
      title: 'Statistics',
    },
  },
};

export default learn;
